<script setup lang="ts">
import { type FunctionalComponent, type Ref, computed, onMounted, ref, watch } from 'vue';
import draggable from 'vuedraggable';

import CopilotActivationService from '@/core/jobs/copilot-activation/copilot-activation.service';
import DragDotsSvg from '@/assets/svg/jobs/drag-dots.svg?component';
import TrashIcon from '@/assets/svg/conversations/TrashBigIcon.svg?component';
import PlusIcon from '@/assets/svg/plus-16.svg?component';
import GenerateScreenerQuestionsAnimation from '@/components/Jobs/CopilotActivation/GenerateScreenerQuestionsAnimation.vue';
import FrequentQuestionsEmptySvg from '@/assets/svg/frequent-questions-empty.svg?component';
import CopilotLightningBlackSvg from '@/assets/svg/jobs/copilot-lightning-black.svg?component';
import CopilotShade800Svg from '@/assets/svg/copilot-shade-800.svg?component';
import {
  type LocalScreenerQuestion,
  MINIMUM_REQUIRED_SCREENER_QUESTIONS,
} from '@/core/jobs/copilot-activation/types/local-screener-question.type';
import MessageTemplate from '@/components/Shared/Input/Template/MessageTemplate.vue';
import { clone } from '@/utils/objects/clone.util';
import TrackingService from '@/core/shared/tracking/tracking.service';
import { TrackingActionName } from '@/core/shared/tracking/tracking-actions';
import VersionsService from '@/core/jobs/versions/versions.service';
import { JobVersionViewStatus } from '@/core/jobs/versions/types/job-version-history.type';
import { ScreeningType } from '@factoryfixinc/ats-interfaces';
import ConfirmScreeningPreemption from './ConfirmScreeningPreemption.vue';
import ProjectService from '@/core/shared/project/project.service';
import EmployerScreeningQuestionsService from '@/core/conversations/employer-screening-questions/employer-screening-questions.service';

const copilotActivationService = new CopilotActivationService();
const jobVersionsService = new VersionsService();
const projectService = new ProjectService();
const employerScreeningQuestionsService = new EmployerScreeningQuestionsService();

const props = defineProps<{
  disabled: boolean;
}>();

const suggestions = ref<LocalScreenerQuestion[]>([]);

const openScreeningChangeWarningDialog = ref(false);
const userAcceptedChangesWarning = ref(false);
const enableScreeningChangeWarning = ref(false);
const intendedScreenerQuestions = ref<LocalScreenerQuestion[]>([]);

type TabId = 'frequentlyUsed' | 'copilotSuggestions';

const selectedTab: Ref<TabId> = ref('frequentlyUsed');
const tabs: { id: TabId; label: string; icon?: FunctionalComponent }[] = [
  { id: 'frequentlyUsed', label: 'Frequently used questions' },
  { id: 'copilotSuggestions', label: 'Copilot suggestions' },
];

async function handleTabChange(tab: TabId) {
  selectedTab.value = tab;
  if (tab === 'copilotSuggestions' && !hasSuggestions.value) {
    await handleGenerateSuggestions();
  } else {
    copilotActivationService.stopGeneratingScreenerQuestions();
  }
}

/** Do not persist new changes until the user accepts the consequences */
const screenerQuestions = computed<LocalScreenerQuestion[]>({
  get: () => copilotActivationService.screenerQuestions.map(getEditableQuestion),
  set: (value) => {
    const copilotEnabled = projectService.currentProject?.copilot;
    if (!userAcceptedChangesWarning.value && enableScreeningChangeWarning.value && copilotEnabled) {
      openScreeningChangeWarningDialog.value = true;
      userAcceptedChangesWarning.value = true;
      intendedScreenerQuestions.value = value;
      return;
    }
    copilotActivationService.screenerQuestions = value.map((question) => {
      return {
        text: question.editContent, // copy edited content to model text
        id: question.id,
        editContent: question.editContent,
      };
    });
  },
});

onMounted(() => {
  enableScreeningChangeWarning.value = true;
  employerScreeningQuestionsService.fetchFrequentlyUsedQuestions();
});

function confirmQuestionChanges() {
  openScreeningChangeWarningDialog.value = false;
  copilotActivationService.screenerQuestions = intendedScreenerQuestions.value;
}

function cancelQuestionChanges() {
  openScreeningChangeWarningDialog.value = false;
  userAcceptedChangesWarning.value = false;
}

const computedFrequentlyUsedQuestions = computed(() => {
  return employerScreeningQuestionsService.frequentlyUsedQuestions
    .map((question) => {
      const addingQuestion = getNewQuestion();
      addingQuestion.text = question.text;
      addingQuestion.editContent = question.text;
      return addingQuestion;
    })
    .filter((question) => !screenerQuestions.value.some((q) => q.text === question.text));
});

const isGeneratingScreenerQuestions = computed(() => {
  return copilotActivationService.isGeneratingScreenerQuestions;
});
const generatedScreenerQuestionsDocument = computed(() => {
  return copilotActivationService.generatedScreenerQuestionsDocument;
});

const fetchedSuggestions = computed(() => {
  return (
    generatedScreenerQuestionsDocument.value?.screeningQuestions?.generatedScreeningQuestions ?? []
  );
});

const availableSuggestions = computed<LocalScreenerQuestion[]>(() => {
  const selectedQuestionsTexts = screenerQuestions.value.map((question) => question.text);

  const availableSuggestions = suggestions.value.filter((suggestion) => {
    return !selectedQuestionsTexts.includes(suggestion.text);
  });

  return availableSuggestions;
});

const hasSuggestions = computed<boolean>(() => {
  return availableSuggestions.value.length > 0;
});

const areScreenerQuestionsValid = computed<boolean>(() => {
  if (copilotActivationService.screeningType === ScreeningType.DYNAMIC) {
    return true;
  }
  const validQuestions = screenerQuestions.value.filter(
    (question) => question.text.trim().length > 0,
  );

  return validQuestions.length >= MINIMUM_REQUIRED_SCREENER_QUESTIONS;
});

const hasTriedToUpdateJob = computed(() => {
  return copilotActivationService.hasTriedToUpdateJob;
});

const shouldShowError = computed<boolean>(() => {
  return hasTriedToUpdateJob.value && !areScreenerQuestionsValid.value;
});

function cleanQuestion(id: number) {
  const localQuestions = clone(screenerQuestions.value);
  const index = localQuestions.findIndex((question) => question.id === id);
  localQuestions[index].text = '';

  screenerQuestions.value = localQuestions;
}

function removeQuestion(id: number) {
  // Remove the questions and then update the array for the reactivity
  const newArray = screenerQuestions.value.filter((question) => question.id !== id);
  screenerQuestions.value = newArray;
}

function getNewQuestion(): LocalScreenerQuestion {
  const ids = screenerQuestions.value.map((question) => question.id);
  const suggestionIds = suggestions.value.map((question) => question.id);
  const usedIds = [...ids, ...suggestionIds];

  let newId = Math.floor(Math.random() * 1000);
  while (usedIds.includes(newId)) {
    newId = Math.floor(Math.random() * 1000);
  }

  const question = {
    text: '',
    editContent: '',
    id: newId,
  };

  return question;
}

function addNewQuestion() {
  const newQuestion = getNewQuestion();
  screenerQuestions.value = [...screenerQuestions.value, newQuestion];
}

function getEditableQuestion(question: LocalScreenerQuestion) {
  return { ...question, editContent: question.text };
}

function addSuggestion(question: LocalScreenerQuestion) {
  // If there is any empty question, we will replace it with the suggestion
  const localQuestions = clone(screenerQuestions.value);
  const emptyQuestionIndex = localQuestions.findIndex((q) => q.text.trim().length === 0);

  if (emptyQuestionIndex !== -1) {
    localQuestions[emptyQuestionIndex] = getEditableQuestion(question);
    screenerQuestions.value = localQuestions;
  } else {
    screenerQuestions.value = [...screenerQuestions.value, getEditableQuestion(question)];
  }
}

function handleQuestionContentChange(changedQuestion: LocalScreenerQuestion) {
  const localQuestions = clone(screenerQuestions.value);
  const index = localQuestions.findIndex((question) => question.id === changedQuestion.id);
  localQuestions[index].text = changedQuestion.editContent;
  localQuestions[index].editContent = changedQuestion.editContent;

  screenerQuestions.value = localQuestions;
}

watch(fetchedSuggestions, (value) => {
  const parsedQuestions: LocalScreenerQuestion[] = [];

  if (Array.isArray(value)) {
    for (const question of value) {
      const addingQuestion = getNewQuestion();
      addingQuestion.text = question;
      parsedQuestions.push(addingQuestion);
    }
  }

  suggestions.value = parsedQuestions;
});

async function generateSuggestions() {
  await copilotActivationService.generateScreenerQuestions();
}

async function handleGenerateSuggestions() {
  if (isGeneratingScreenerQuestions.value) {
    copilotActivationService.stopGeneratingScreenerQuestions();
  } else {
    TrackingService.trackAction(TrackingActionName.SCREENERS_GENERATED, {
      project_id: copilotActivationService.selectedProjectId,
      job_id: copilotActivationService.selectedJobId,
    });
    await generateSuggestions();
  }
}
const isJobVersionViewMode = computed(() => {
  return jobVersionsService.currentJobVersionViewStatus === JobVersionViewStatus.VIEW_MODE;
});

const isDisabled = computed(() => {
  return props.disabled || screenerQuestions.value.length <= MINIMUM_REQUIRED_SCREENER_QUESTIONS;
});
</script>

<template>
  <div>
    <draggable
      v-model="screenerQuestions"
      itemKey="id"
      group="textareas"
      class="mb-6"
      handle=".question-draggable-handle"
    >
      <template #item="{ element }: { element: LocalScreenerQuestion }">
        <v-text-field
          v-model.trim="element.editContent"
          placeholder="Add screening question"
          variant="outlined"
          density="compact"
          class="mb-[30px] last:mb-0"
          hide-details
          @blur="() => handleQuestionContentChange(element)"
          :disabled="disabled"
        >
          <!-- outer drag | delete icons -->
          <!-- Drag -->
          <template #prepend>
            <div class="question-draggable-handle">
              <DragDotsSvg />
            </div>
          </template>

          <!-- Clean -->
          <template #append-inner>
            <button class="clean-btn cursor-pointer" @click="cleanQuestion(element.id)">
              <PlusIcon class="rotate-45" />
            </button>
          </template>

          <!-- Delete -->
          <template #append>
            <button
              class="disabled:hidden"
              :disabled="isDisabled"
              @click="removeQuestion(element.id)"
            >
              <TrashIcon class="text-shade-800" />
            </button>
          </template>
        </v-text-field>
      </template>
    </draggable>
    <div v-if="shouldShowError" class="v-field--error -mt-6 mb-10 ml-14">
      <MessageTemplate
        message="Add at least one screener question or select the option to Skip Screening."
        class="text-[#B00020]"
      />
    </div>

    <!-- Add questions -->
    <div>
      <div class="add-question-div">
        <button
          v-show="!isJobVersionViewMode"
          class="flex h-10 w-full items-center justify-center rounded-md bg-shade-900 disabled:opacity-50"
          type="button"
          @click="addNewQuestion()"
        >
          <span>
            <PlusIcon class="mr-2 text-tint-20" />
          </span>
          <span class="text-sm font-semibold leading-5 text-tint-20"> Add question </span>
        </button>
      </div>
    </div>

    <!-- Tabs -->
    <div>
      <div class="my-6 flex text-sm font-bold">
        <button
          v-for="tab in tabs"
          v-show="!isJobVersionViewMode"
          :key="tab.id"
          class="flex flex-1 items-center justify-center border-b-2 pb-3"
          :class="{
            'border-shade-880 text-shade-880': selectedTab === tab.id,
            'border-tint-100 text-shade-800': selectedTab !== tab.id,
          }"
          @click="handleTabChange(tab.id)"
        >
          <component
            v-if="tab.id === 'copilotSuggestions'"
            :is="selectedTab === tab.id ? CopilotLightningBlackSvg : CopilotShade800Svg"
            class="mr-2"
          />
          {{ tab.label }}
        </button>
      </div>

      <!-- Content for Frequently Used Questions tab -->
      <div v-if="selectedTab === 'frequentlyUsed'" class="h-[300px] overflow-y-auto">
        <!-- Empty state -->
        <div v-if="!computedFrequentlyUsedQuestions.length" class="mt-6 text-center text-sm">
          <p class="mb-4 font-bold text-shade-800">
            <FrequentQuestionsEmptySvg class="mx-auto mb-10" />
            Your team's most-used screening questions will appear here as you create jobs
          </p>
          <p
            class="cursor-pointer font-semibold text-highlight-500"
            @click="handleTabChange('copilotSuggestions')"
          >
            Get quick question ideas from Copilot
          </p>
        </div>
        <!-- Questions -->
        <div v-for="question in computedFrequentlyUsedQuestions" :key="question.id">
          <button
            class="mb-2 flex cursor-pointer items-center justify-between rounded-full border border-tint-80 px-3 py-1.5 hover:border-tint-20 disabled:opacity-50"
            @click="addSuggestion(question)"
          >
            <div class="inline-block">
              <span>{{ question.text }}</span>
            </div>
            <div class="inline-block">
              <PlusIcon class="ml-2" />
            </div>
          </button>
        </div>
      </div>

      <!-- Content for Copilot Suggestions tab -->
      <!-- NOTE: use v-show instead of v-if to avoid re-rendering the animation -->
      <div v-show="selectedTab === 'copilotSuggestions'" class="h-[300px] overflow-y-auto">
        <GenerateScreenerQuestionsAnimation v-show="!hasSuggestions" class="mt-6" />
        <div v-if="hasSuggestions" class="mb-10">
          <div v-for="suggestion in availableSuggestions" :key="suggestion.id">
            <button
              class="mb-2 flex cursor-pointer items-center justify-between rounded-full border border-tint-80 px-3 py-1.5 hover:border-tint-20 disabled:opacity-50"
              @click="addSuggestion(suggestion)"
            >
              <div class="inline-block">
                <span>{{ suggestion.text }}</span>
              </div>
              <div class="inline-block">
                <PlusIcon class="ml-2" />
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>

    <ConfirmScreeningPreemption
      @save="confirmQuestionChanges"
      @cancel="cancelQuestionChanges"
      :is-open="openScreeningChangeWarningDialog"
      :is-loading="false"
      v-if="openScreeningChangeWarningDialog"
      confirm-action="Confirm change"
    />
  </div>
</template>

<style lang="scss" scoped>
.v-input :deep(.v-field),
.v-input--horizontal :deep(.v-field) {
  border-radius: 6px;
  background-color: white;

  .v-field__outline {
    @apply text-tint-80;
  }
  input {
    @apply font-sans text-sm font-normal leading-[21px] text-shade-880;
    cursor: text;
    user-select: text;
  }
  input::placeholder {
    @apply text-shade-800;
    opacity: 1;
  }

  .clean-btn {
    display: none;
  }

  &:hover {
    .v-field__outline {
      @apply text-shade-840;
    }

    .clean-btn {
      display: block;
    }
  }
}

.v-input :deep(.v-field.v-field--focused),
.v-input--horizontal :deep(.v-field.v-field--focused) {
  .v-field__outline {
    @apply text-highlight-500;
  }
}

.question-draggable-handle {
  @apply cursor-grab;

  &:active {
    @apply cursor-grabbing;
  }
}
</style>
